<script setup lang="ts">
import { computed, ref } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useTheme } from "@/lib/theme"
import { initials } from "@/lib/format"
import { translate, translateRole } from "@/i18n"
import LocaleSwitcher from "@/components/LocaleSwitcher.vue"
import { useAuthStore } from "@/stores/auth"
import { useToastStore } from "@/stores/toasts"

interface NavItem { to: string; label: string; icon: string; admin?: boolean; minimum?: "SUPPORT" | "ADMIN" | "SUPER_ADMIN" }

const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const toasts = useToastStore()
const { isDark, toggle } = useTheme()
const menuOpen = ref(false)
const busy = ref(false)

const customerNavigation: NavItem[] = [
  { to: "/dashboard", label: "Overview", icon: "⌂" },
  { to: "/dashboard/wallet", label: "Wallet", icon: "$" },
  { to: "/dashboard/payments", label: "Payments", icon: "↗" },
  { to: "/dashboard/api-keys", label: "API keys", icon: "⌁" },
  { to: "/dashboard/usage", label: "Usage", icon: "⌇" },
  { to: "/dashboard/sessions", label: "Sessions", icon: "◉" },
  { to: "/dashboard/security", label: "Security", icon: "◇" },
  { to: "/dashboard/settings", label: "Settings", icon: "⚙" },
]
const operatorNavigation: NavItem[] = [
  { to: "/admin", label: "Overview", icon: "⌂", minimum: "SUPPORT" },
  { to: "/admin/accounts", label: "Accounts", icon: "◎", minimum: "SUPPORT" },
  { to: "/admin/payments", label: "Payments", icon: "$", minimum: "SUPPORT" },
  { to: "/admin/usage", label: "Usage explorer", icon: "⌇", minimum: "SUPPORT" },
  { to: "/admin/statistics", label: "Statistics", icon: "↗", minimum: "SUPPORT" },
  { to: "/admin/models", label: "Models", icon: "◈", minimum: "ADMIN" },
  { to: "/admin/payment-events", label: "Payment events", icon: "↻", minimum: "SUPPORT" },
  { to: "/admin/audit-log", label: "Audit log", icon: "≡", minimum: "SUPPORT" },
  { to: "/admin/jobs", label: "Jobs", icon: "⚙", minimum: "ADMIN" },
  { to: "/admin/mail", label: "Mail", icon: "✉", minimum: "ADMIN" },
  { to: "/admin/system", label: "System", icon: "◇", minimum: "SUPPORT" },
]
const inAdmin = computed(() => route.path.startsWith("/admin"))
const navigation = computed(() => (inAdmin.value ? operatorNavigation : customerNavigation)
  .filter((item) => !item.minimum || auth.hasRole(item.minimum)))

async function logout(): Promise<void> {
  busy.value = true
  try {
    await auth.logout()
    await router.replace("/login")
  } catch {
    toasts.push(translate("Your local session was cleared. The server could not confirm logout."), "error")
    await router.replace("/login")
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <div class="app-layout">
    <button class="mobile-menu-button" type="button" :aria-label="$t('Open navigation')" @click="menuOpen = !menuOpen">☰</button>
    <aside class="sidebar" :class="{ 'sidebar-open': menuOpen }">
      <RouterLink class="brand sidebar-brand" to="/"><span class="brand-mark">C</span>{{ $t("CodexNow") }}</RouterLink>
      <div class="workspace-switcher">
        <span>{{ inAdmin ? $t("Operator console") : $t("Customer workspace") }}</span>
        <RouterLink v-if="auth.isOperator" :to="inAdmin ? '/dashboard' : '/admin'">
          {{ inAdmin ? $t("Customer view") : $t("Operator console") }} →
        </RouterLink>
      </div>
      <nav class="sidebar-nav" :aria-label="inAdmin ? $t('Operator navigation') : $t('Dashboard navigation')">
        <RouterLink v-for="item in navigation" :key="item.to" :to="item.to" @click="menuOpen = false">
          <span aria-hidden="true">{{ item.icon }}</span>{{ $t(item.label) }}
        </RouterLink>
      </nav>
      <div class="sidebar-footer">
        <LocaleSwitcher />
        <button class="theme-row" type="button" @click="toggle">
          <span>{{ isDark ? "☀" : "☾" }}</span>{{ isDark ? $t("Light theme") : $t("Dark theme") }}
        </button>
        <div class="profile-row">
          <span class="avatar">{{ initials(auth.user?.displayName ?? "") }}</span>
          <div><strong>{{ auth.user?.displayName }}</strong><small>{{ translateRole(auth.user?.role) }}</small></div>
          <button class="icon-button" type="button" :disabled="busy" :aria-label="$t('Sign out')" @click="logout">↪</button>
        </div>
      </div>
    </aside>
    <button v-if="menuOpen" class="sidebar-scrim" type="button" :aria-label="$t('Close navigation')" @click="menuOpen = false" />
    <main class="app-main"><RouterView /></main>
  </div>
</template>
